"use client"

import { useState, useEffect } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { DiscountMatrixTable } from "./results-panel"
import { PortfolioSummary } from "./portfolio-summary"
import type { PortfolioData } from "@/lib/api-service"

interface PortfolioContentProps {
  portfolio: PortfolioData
}

// Removed Table 1: Volatility and Half-Life Statistics block per request

export function PortfolioContent({ portfolio }: PortfolioContentProps) {
  const [activeTab, setActiveTab] = useState("blockage")

  // Check if user input is available for this portfolio
  const hasUserInput = portfolio.hasUserInput

  useEffect(() => {
    if (hasUserInput) {
      setActiveTab("userInput")
    } else if (!hasUserInput && activeTab === "userInput") {
      setActiveTab("blockage")
    }
  }, [hasUserInput, activeTab])

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-xl font-semibold mb-2">{portfolio.name} Portfolio</h3>
        <p className="text-muted-foreground">{portfolio.description}</p>
      </div>

      <PortfolioSummary portfolio={portfolio} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="no-print">
        {hasUserInput ? (
          <TabsList className="grid w-full grid-cols-1">
            <TabsTrigger value="userInput">User Input Mode</TabsTrigger>
          </TabsList>
        ) : (
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="blockage">Blockage Discount</TabsTrigger>
            <TabsTrigger value="marketability">Transfer Restrictions</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="blockage" className="space-y-4">
          <div className="mb-4">
            <h4 className="text-lg font-semibold mb-2">Blockage Discount: No Transfer Restrictions</h4>
            <p className="text-muted-foreground">
              These tables show discount percentages when there are no regulatory restrictions on liquidation.
            </p>
          </div>

          <DiscountMatrixTable
            data={portfolio.matrices.discountAASP}
            title="Asian Average Exchange Option (AAE)"
            description="Applicable when the buyer and seller have access to all information, public and private, about the firm. No information asymmetry. For example, restricted stock granted to insiders."
          />

          <DiscountMatrixTable
            data={portfolio.matrices.discountBSP}
            title="Margrabe Exchange Option (ME)"
            description="Applicable when the buyer and seller have access to all public information about the firm. No information asymmetry. Transaction between outsiders."
          />

          <DiscountMatrixTable
            data={portfolio.matrices.discountLBP}
            title="Look Back Exchange Option (LBE)"
            description="Applicable when the buyer has access to all public information about the firm. The seller has access to all information, public and private, about the firm. Information asymmetry exists. For example, restricted stock placed with an outsider investor."
          />
        </TabsContent>

        <TabsContent value="marketability" className="space-y-4">
          <div className="mb-4">
            <h4 className="text-lg font-semibold mb-2">
              Discount for Lack of Marketability: Transfer Restrictions in Place
            </h4>
            <p className="text-muted-foreground">
              These tables show discount percentages when Rule 144 transfer restrictions are in place, adding holding
              period requirements to liquidation periods.
            </p>
          </div>

          <DiscountMatrixTable
            data={portfolio.matrices.discountAASPLP}
            title="Asian Average Exchange Option (AAE) with Restrictions"
            description="Same as AAE (No Restrictions) but with transfer restrictions applied. Liquidation periods are extended based on Rule 144 requirements."
          />

          <DiscountMatrixTable
            data={portfolio.matrices.discountBSPLP}
            title="Margrabe Exchange Option (ME) with Restrictions"
            description="Same as ME (No Restrictions) but with transfer restrictions applied. Liquidation periods are extended based on Rule 144 requirements."
          />

          <DiscountMatrixTable
            data={portfolio.matrices.discountLBPLP}
            title="Look Back Exchange Option (LBE) with Restrictions"
            description="Same as LBE (No Restrictions) but with transfer restrictions applied. Liquidation periods are extended based on Rule 144 requirements."
          />
        </TabsContent>

        {hasUserInput && (
          <TabsContent value="userInput" className="space-y-4">
            <div className="mb-4">
              <h4 className="text-lg font-semibold mb-2">User Input Mode: Custom Volatility and Liquidation</h4>
              <p className="text-muted-foreground">These tables use user-provided volatility and liquidation period inputs.</p>
            </div>

            <DiscountMatrixTable
              data={portfolio.matricesUI.discountAASPUI!}
              title="Asian Average Exchange Option (AAE) — User Input"
              description="No transfer restrictions; values computed from user-provided inputs."
            />

            <DiscountMatrixTable
              data={portfolio.matricesUI.discountBSPUI!}
              title="Margrabe Exchange Option (ME) — User Input"
              description="No transfer restrictions; values computed from user-provided inputs."
            />

            <DiscountMatrixTable
              data={portfolio.matricesUI.discountLBPUI!}
              title="Look Back Exchange Option (LBE) — User Input"
              description="No transfer restrictions; values computed from user-provided inputs."
            />

            <DiscountMatrixTable
              data={portfolio.matricesUI.discountAASPLPUI!}
              title="Asian Average Exchange Option (AAE) with Restrictions — User Input"
              description="With transfer restrictions applied; values computed from user-provided inputs."
            />

            <DiscountMatrixTable
              data={portfolio.matricesUI.discountBSPLPUI!}
              title="Margrabe Exchange Option (ME) with Restrictions — User Input"
              description="With transfer restrictions applied; values computed from user-provided inputs."
            />

            <DiscountMatrixTable
              data={portfolio.matricesUI.discountLBPLPUI!}
              title="Look Back Exchange Option (LBE) with Restrictions — User Input"
              description="With transfer restrictions applied; values computed from user-provided inputs."
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
